import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// --- BASE API URL (FIXED) ---
const baseUrl = window.BASE_API_URL || "https://fosv2.foodship.net/api/fos/";
const API_URL = baseUrl.replace(/\/+$/, "") + "/"; // ensures exactly 1 trailing slash

// --- Safe user restore from localStorage ---
let savedUser = null;
try {
  const rawUser = localStorage.getItem("user");
  if (rawUser && rawUser !== "undefined" && rawUser !== "null") {
    savedUser = JSON.parse(rawUser);
  }
} catch {
  savedUser = null;
}

// ======================================================================================
// LOGIN
// ======================================================================================
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}login`, { email, password });

      localStorage.setItem("token", data.token);
      localStorage.setItem(
        "user",
        JSON.stringify({
          id: data.id,
          name: data.name,
          email: data.email,
          phone: data.phone,
          token: data.token,
        })
      );
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;

      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Login failed");
    }
  }
);

// ======================================================================================
// SEND OTP (REGISTER)
// ======================================================================================
export const sendOtp = createAsyncThunk(
  "auth/sendOtp",
  async ({ email }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}send-otp`, { email });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to send OTP");
    }
  }
);

// ======================================================================================
// VERIFY OTP & REGISTER
// ======================================================================================
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ name, email, password, otp, phone }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}verify-otp`, {
        name,
        email,
        password,
        phone,
        otp,
      });

      localStorage.setItem("token", data.token);
      localStorage.setItem(
        "user",
        JSON.stringify({
          id: data.id,
          name: data.name,
          email: data.email,
          phone: data.phone,
          token: data.token,
        })
      );
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;

      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to verify OTP");
    }
  }
);

// ======================================================================================
// FORGOT PASSWORD — SEND OTP
// ======================================================================================
export const sendForgotOtp = createAsyncThunk(
  "auth/sendForgotOtp",
  async ({ email }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}forgot-password/send-otp`, {
        email,
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to send OTP");
    }
  }
);

// ======================================================================================
// FORGOT PASSWORD — VERIFY OTP & RESET PASSWORD
// ======================================================================================
export const verifyForgotOtp = createAsyncThunk(
  "auth/verifyForgotOtp",
  async ({ email, otp, newPassword }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}forgot-password/verify-otp`, {
        email,
        otp,
        password: newPassword,
      });

      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to reset password");
    }
  }
);

// ======================================================================================
// UPDATE PROFILE
// ======================================================================================
export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async ({ name, phone, password, confirm_password, token }, { rejectWithValue }) => {
    try {
      const payload = { name, phone, token };

      if (password && confirm_password) {
        payload.password = password;
        payload.confirm_password = confirm_password;
      }

      const { data } = await axios.post(`${API_URL}update-profile`, payload);

      if (!data.success) {
        return rejectWithValue(data.message || "Failed to update profile");
      }

      const storedUser = JSON.parse(localStorage.getItem("user")) || {};
      const updatedUser = { ...storedUser, name, phone };

      localStorage.setItem("user", JSON.stringify(updatedUser));

      return updatedUser;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Profile update failed");
    }
  }
);

// ======================================================================================
// SLICE
// ======================================================================================
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: savedUser,
    token: localStorage.getItem("token") || null,
    loading: false,
    error: null,
    otpSent: false,
    otpVerified: false,

    // ❗Important for Forgot Password
    forgotOtpSent: false,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.otpSent = false;
      state.otpVerified = false;
      state.forgotOtpSent = false;

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("cart");
      localStorage.removeItem("editComboItem");
      localStorage.removeItem("orderService");
      localStorage.removeItem("dinein");

      delete axios.defaults.headers.common["Authorization"];
    },

    setCredentials: (state, action) => {
      const { user, token } = action.payload;
      state.user = user;
      state.token = token;

      if (token) {
        localStorage.setItem("token", token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // ===================
      // LOGIN
      // ===================
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = {
          id: action.payload.id,
          name: action.payload.name,
          email: action.payload.email,
          phone: action.payload.phone,
          token: action.payload.token,
        };
        state.token = action.payload.token;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===================
      // REGISTER — SEND OTP
      // ===================
      .addCase(sendOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state) => {
        state.loading = false;
        state.otpSent = true;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===================
      // REGISTER — VERIFY OTP
      // ===================
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.otpVerified = true;
        state.user = {
          id: action.payload.id,
          name: action.payload.name,
          email: action.payload.email,
          phone: action.payload.phone,
          token: action.payload.token,
        };
        state.token = action.payload.token;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===================
      // FORGOT PASSWORD — SEND OTP
      // ===================
      .addCase(sendForgotOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendForgotOtp.fulfilled, (state) => {
        state.loading = false;
        state.forgotOtpSent = true;
      })
      .addCase(sendForgotOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===================
      // FORGOT PASSWORD — VERIFY OTP + RESET
      // ===================
      .addCase(verifyForgotOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyForgotOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyForgotOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===================
      // UPDATE PROFILE
      // ===================
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = { ...state.user, ...action.payload };
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, setCredentials } = authSlice.actions;
export default authSlice.reducer;
